/**
 * Sheet — right-side drawer primitive.
 *
 * Sibling to Modal. Same Radix Dialog under the hood (focus trap, Esc,
 * portal, scroll lock), but the Content slides in from the right edge
 * with a sticky header (title + close) and an optional sticky footer
 * (action buttons). Use for context-rich workflows that want to leave
 * the page visible behind the panel — Run-a-Job is the canonical case.
 *
 * Use Modal when the workflow is a focused decision (confirm, picker)
 * with no relationship to what's behind it.
 */

import { type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { t } from '@/lib/i18n';
import styles from './Sheet.module.css';

type SheetSize = 'sm' | 'md' | 'lg';

const SIZE_CLASS: Record<SheetSize, string | undefined> = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
};

export interface SheetProps {
  /** Controls visibility. */
  open: boolean;
  /** Called when the sheet would like to close — Esc, backdrop click, etc. */
  onOpenChange: (open: boolean) => void;
  /** Accessible name for the dialog (rendered as the visible heading). */
  title: ReactNode;
  /** Optional explainer text shown under the title. */
  description?: ReactNode;
  /** Custom label for the close × button. Defaults to "Close". */
  closeLabel?: string;
  /** Body content. Scrolls if it overflows. */
  children?: ReactNode;
  /** Sticky footer (typically Cancel / Submit buttons). */
  footer?: ReactNode;
  /**
   * Width preset.
   *  - sm (400px)
   *  - md (520px, default)
   *  - lg (720px)
   * On viewports narrower than the preset, expands to fill the screen.
   */
  size?: SheetSize;
}

export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  closeLabel,
  children,
  footer,
  size = 'md',
}: SheetProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={[styles.content, SIZE_CLASS[size]].filter(Boolean).join(' ')}>
          <div className={styles.header}>
            <div className={styles.headerText}>
              <Dialog.Title className={styles.title}>{title}</Dialog.Title>
              {description ? (
                <Dialog.Description className={styles.description}>
                  {description}
                </Dialog.Description>
              ) : null}
            </div>
            <Dialog.Close asChild>
              <button type="button" className={styles.close} aria-label={closeLabel ?? t('Close')}>
                <span aria-hidden="true">×</span>
              </button>
            </Dialog.Close>
          </div>
          {children ? <div className={styles.body}>{children}</div> : null}
          {footer ? <div className={styles.footer}>{footer}</div> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
